<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link
        view="AdminReimbursementAggregationList"
        label="Reimbursement Aggregations"
        :active="true"
      />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>

      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title">Reimbursement Aggregation List</h1>
          </div>
        </div>
      </div>
      <hr />

      <!-- Filter Section -->
      <div class="filter-section">
        <div class="columns is-multiline">
          <div class="column is-4">
            <b-field label="Organization">
              <b-select
                v-model="selectedOrganizationID"
                placeholder="All Organizations"
                :loading="loadingOrganizations"
                @input="handleOrganizationChange"
                expanded
              >
                <option :value="0">All Organizations</option>
                <option v-for="org in organizationOptions" :value="org.id" :key="org.id">
                  {{ org.name }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-4">
            <b-field label="User">
              <b-select
                v-model="selectedUserID"
                placeholder="All Users"
                :loading="loadingUsers"
                :disabled="selectedOrganizationID === 0"
                @input="handleUserChange"
                expanded
              >
                <option :value="0">All Users</option>
                <option v-for="user in userOptions" :value="user.id" :key="user.id">
                  {{ user.first_name }} {{ user.last_name }}
                </option>
              </b-select>
            </b-field>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div>
        <b-table
          :data="aggregations"
          :loading="loading"
          :paginated="true"
          :per-page="pageSize"
          :current-page="currentPage"
          :total="totalItems"
          backend-pagination
          @page-change="handlePageChange"
          striped
          hoverable
          detailed
          detail-key="id"
          :show-detail-icon="true"
        >
          <b-table-column field="organization" label="Organization" v-slot="props" sortable>
            <span v-if="props.row.organization">
              {{ props.row.organization.name }}
            </span>
            <span v-else>-</span>
          </b-table-column>

          <b-table-column field="user" label="User" v-slot="props" sortable>
            <span v-if="props.row.user">
              {{ props.row.user.first_name }} {{ props.row.user.last_name }}
            </span>
            <span v-else>-</span>
          </b-table-column>

          <b-table-column field="status" label="Status" v-slot="props" width="150" sortable>
            <b-tag
              :type="
                props.row.status === 'has_data'
                  ? 'is-success'
                  : props.row.status === 'local_allowable'
                    ? 'is-info'
                    : 'is-warning'
              "
            >
              {{
                props.row.status === 'has_data'
                  ? 'Has Data'
                  : props.row.status === 'local_allowable'
                    ? 'Local Allowable'
                    : 'No Data'
              }}
            </b-tag>
          </b-table-column>

          <b-table-column
            field="average"
            label="Average"
            v-slot="props"
            width="100"
            numeric
            sortable
          >
            <span v-if="props.row.average !== null && props.row.average !== undefined">
              {{ props.row.average.toFixed(2) }}%
            </span>
            <span v-else>-</span>
          </b-table-column>

          <b-table-column label="DTX" v-slot="props" width="80" numeric>
            <span v-if="props.row.dtx !== null && props.row.dtx !== undefined">
              {{ props.row.dtx.toFixed(2) }}%
            </span>
            <span v-else>-</span>
          </b-table-column>

          <b-table-column label="RTC" v-slot="props" width="80" numeric>
            <span v-if="props.row.rtc !== null && props.row.rtc !== undefined">
              {{ props.row.rtc.toFixed(2) }}%
            </span>
            <span v-else>-</span>
          </b-table-column>

          <b-table-column label="PHP" v-slot="props" width="80" numeric>
            <span v-if="props.row.php !== null && props.row.php !== undefined">
              {{ props.row.php.toFixed(2) }}%
            </span>
            <span v-else>-</span>
          </b-table-column>

          <b-table-column label="IOP" v-slot="props" width="80" numeric>
            <span v-if="props.row.iop !== null && props.row.iop !== undefined">
              {{ props.row.iop.toFixed(2) }}%
            </span>
            <span v-else>-</span>
          </b-table-column>

          <b-table-column label="OP" v-slot="props" width="80" numeric>
            <span v-if="props.row.op !== null && props.row.op !== undefined">
              {{ props.row.op.toFixed(2) }}%
            </span>
            <span v-else>-</span>
          </b-table-column>

          <b-table-column field="created_at" label="Created At" v-slot="props" width="160" sortable>
            {{ formatDate(props.row.created_at) }}
          </b-table-column>

          <!-- Detailed row template for expanded content -->
          <template #detail="props">
            <p>
              <strong>ID:</strong>
              {{ props.row.id }}
              <br />
              <strong>Organization ID:</strong>
              {{ props.row.organization_id }}
              <br />
              <strong>User ID:</strong>
              {{ props.row.user_id }}
              <br />
              <strong>User:</strong>
              <span v-if="props.row.user">
                {{ props.row.user.first_name }} {{ props.row.user.last_name }}
              </span>
              <span v-else>-</span>
              <br />
              <strong>Created At:</strong>
              {{ formatDateFull(props.row.created_at) }}
              <br />
              <strong>Status:</strong>
              {{ props.row.status === 'has_data' ? 'Has Data' : 'No Data' }}
              <br />
              <strong>Payer:</strong>
              {{ props.row.payer ? `${props.row.payer.name} (ID: ${props.row.payer_id})` : '-' }}
              <br />
              <strong>Prefix:</strong>
              {{
                props.row.prefix ? `${props.row.prefix.prefix} (ID: ${props.row.prefix_id})` : '-'
              }}
              <br />
              <strong>Group:</strong>
              {{ props.row.group ? `${props.row.group.name} (ID: ${props.row.group_id})` : '-' }}
              <br />
              <strong>Employer:</strong>
              {{
                props.row.employer
                  ? `${props.row.employer.name} (ID: ${props.row.employer_id})`
                  : '-'
              }}
              <br />
              <strong>DTX:</strong>
              {{
                props.row.dtx !== null && props.row.dtx !== undefined
                  ? props.row.dtx.toFixed(2) + '%'
                  : '-'
              }}
              <br />
              <strong>RTC:</strong>
              {{
                props.row.rtc !== null && props.row.rtc !== undefined
                  ? props.row.rtc.toFixed(2) + '%'
                  : '-'
              }}
              <br />
              <strong>PHP:</strong>
              {{
                props.row.php !== null && props.row.php !== undefined
                  ? props.row.php.toFixed(2) + '%'
                  : '-'
              }}
              <br />
              <strong>IOP:</strong>
              {{
                props.row.iop !== null && props.row.iop !== undefined
                  ? props.row.iop.toFixed(2) + '%'
                  : '-'
              }}
              <br />
              <strong>OP:</strong>
              {{
                props.row.op !== null && props.row.op !== undefined
                  ? props.row.op.toFixed(2) + '%'
                  : '-'
              }}
              <br />
              <strong>Average:</strong>
              {{
                props.row.average !== null && props.row.average !== undefined
                  ? props.row.average.toFixed(2) + '%'
                  : '-'
              }}
            </p>
          </template>

          <template #empty>
            <div class="has-text-centered">No reimbursement aggregations found.</div>
          </template>
        </b-table>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from '../../layouts/AdminLayout.vue';
import request from '../../request';

export default {
  name: 'AdminReimbursementAggregationList',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: true,
      aggregations: [],
      currentPage: 1,
      pageSize: 15,
      totalItems: 0,
      // Filter options
      selectedOrganizationID: 0,
      selectedUserID: 0,
      organizationOptions: [],
      userOptions: [],
      loadingOrganizations: false,
      loadingUsers: false,
    };
  },
  mounted() {
    this.fetchOrganizations();
    this.fetchAggregations();
  },
  methods: {
    async fetchOrganizations() {
      this.loadingOrganizations = true;
      try {
        const resp = await request('/admin/organizations');
        this.organizationOptions = resp.data.organizations.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load organizations.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loadingOrganizations = false;
      }
    },
    async fetchUsers(organizationId) {
      this.loadingUsers = true;
      try {
        const resp = await request(`/admin/organizations/${organizationId}/users`);
        this.userOptions = resp.data.users.sort((a, b) =>
          `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
        );
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load users.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loadingUsers = false;
      }
    },
    handleOrganizationChange(organizationId) {
      this.selectedUserID = 0;
      this.userOptions = [];

      if (organizationId !== 0) {
        this.fetchUsers(organizationId);
      }

      this.currentPage = 1;
      this.fetchAggregations();
    },
    handleUserChange() {
      this.currentPage = 1;
      this.fetchAggregations();
    },
    fetchAggregations() {
      this.loading = true;

      let args = {
        page: this.currentPage,
        limit: this.pageSize,
      };

      // Add filter parameters
      if (this.selectedOrganizationID !== 0) {
        args.organization_id = this.selectedOrganizationID;
      }
      if (this.selectedUserID !== 0) {
        args.user_id = this.selectedUserID;
      }

      const params = new URLSearchParams(args).toString();
      return request(`/admin/reimbursements/aggregations?${params}`)
        .then((resp) => {
          this.aggregations = resp.data.list;
          const totalCount = resp.headers['x-total-count'];
          if (totalCount) {
            this.totalItems = parseInt(totalCount, 10);
          } else {
            this.totalItems = resp.data.list.length;
          }
          this.loading = false;
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            this.$buefy.toast.open({
              message: 'Session expired. Please log in again.',
              type: 'is-danger',
              duration: 5000,
            });
            this.$router.push({ name: 'login' });
          } else {
            this.$buefy.toast.open({
              message: 'An error occurred while fetching reimbursement aggregations.',
              type: 'is-danger',
              duration: 5000,
            });
          }
          this.loading = false;
        });
    },
    handlePageChange(newPage) {
      this.currentPage = newPage;
      this.fetchAggregations();
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString();
    },
    formatDateFull(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString();
    },
  },
};
</script>

<style scoped>
.filter-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f5f5f5;
  border-radius: 4px;
}
</style>
